import { useLayoutEffect } from 'react';
import type { DataTableBaseColumn, DataTableRow } from '../TableTypes';
import type { DataTableId } from '../utils';

interface UseInitializeMeasureColumnWidthProps {
  filteredAndSortedColumns: DataTableBaseColumn[];
  rows?: DataTableRow[];
  columnWidthsRef: React.MutableRefObject<Record<string, number>>;
  setColumnWidths: (value: Record<string, number>) => void;
  initialWidthsMeasured: boolean;
  setInitialWidthsMeasured: (value: boolean) => void;
  tableId: DataTableId;
}

export const useInitializeMeasureColumnWidth = ({
  filteredAndSortedColumns,
  rows,
  columnWidthsRef,
  setColumnWidths,
  initialWidthsMeasured,
  setInitialWidthsMeasured,
  tableId,
}: UseInitializeMeasureColumnWidthProps) => {
  // Measure initial column widths after table is rendered to ensure consistent
  // column sizing during virtual scrolling. This prevents content from shifting
  // as users scroll through large datasets by capturing the actual rendered
  // widths and applying them to all virtualized rows.
  useLayoutEffect(() => {
    if (
      !initialWidthsMeasured &&
      filteredAndSortedColumns &&
      filteredAndSortedColumns.length > 0 &&
      rows &&
      rows.length > 0
    ) {
      const measureWidths = () => {
        const measuredWidths: Record<string, number> = {};
        let hasValidMeasurements = false;

        filteredAndSortedColumns.forEach((column) => {
          let width = 0;

          // Try to find the header cell first
          const headerCell = document.querySelector(
            `[data-column-id="${column.field}"]`,
          ) as HTMLElement;
          if (headerCell) {
            width = headerCell.getBoundingClientRect().width;
          }

          // If header measurement failed, try to measure from the first table body cell
          if (width <= 0) {
            const firstRowCell = document.querySelector(
              `#table_row_${rows[0]?.id} .SweepDataTableCell:nth-child(${filteredAndSortedColumns.indexOf(column) + 1})`,
            ) as HTMLElement;
            if (firstRowCell) {
              width = firstRowCell.getBoundingClientRect().width;
            }
          }

          // Fallback to column's defined width if DOM measurement fails
          if (width <= 0 && column.width) {
            if (typeof column.width === 'number') {
              width = column.width;
            } else if (typeof column.width === 'string' && column.width.endsWith('px')) {
              width = parseInt(column.width.replace('px', ''), 10);
            }
          }

          if (width > 0) {
            measuredWidths[column.field] = width;
            hasValidMeasurements = true;
          }
        });

        if (hasValidMeasurements) {
          columnWidthsRef.current = measuredWidths;
          setColumnWidths(measuredWidths);
          setInitialWidthsMeasured(true);
        }
      };

      // Use requestAnimationFrame to ensure measurement happens after render
      const timeoutId = setTimeout(() => {
        requestAnimationFrame(measureWidths);
      }, 0);

      return () => clearTimeout(timeoutId);
    }
  }, [
    initialWidthsMeasured,
    filteredAndSortedColumns,
    rows?.length,
    rows,
    columnWidthsRef,
    setColumnWidths,
    setInitialWidthsMeasured,
  ]);

  // Reset measurement flag when tableId change
  useLayoutEffect(() => {
    setInitialWidthsMeasured(false);
  }, [tableId, setInitialWidthsMeasured]);
};
